import { yupResolver } from '@hookform/resolvers/yup';
import React, { useCallback, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { LuFileText } from 'react-icons/lu';
import * as Yup from 'yup';

import { TaxFilingStatus, TaxFilingV2 } from '@ndvr/portfolio-api';

import { FormInput, FormSelect } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { SelectItem } from '@/components/ui/select';
import { titleCase } from 'utils';
import { convertServerDateToFormDate } from 'utils/date-formatter';

interface EditTaxFilingDialogProps {
    filing?: TaxFilingV2;
    onSave: (data: FormValues) => void;
    onCancel: () => void;
    open: boolean;
    isActive?: boolean;
}

const formSchema = Yup.object({
    title: Yup.string()
        .required('Title is required')
        .trim()
        .min(1, 'Title cannot be empty')
        .max(64, 'Title cannot exceed 64 characters'),
    taxFilingStatus: Yup.string()
        .oneOf(Object.values(TaxFilingStatus), 'Filing status is required')
        .required('Filing status is required'),
    effectiveFrom: Yup.string().required('Effective date is required'),
});

export type FormValues = Yup.InferType<typeof formSchema>;

export function EditTaxFilingDialog({ filing, onSave, onCancel, open, isActive }: EditTaxFilingDialogProps) {
    const titleInputRef = useRef<HTMLInputElement | null>(null);

    const getDefaultValues = useCallback((filingData?: TaxFilingV2): FormValues => {
        if (filingData) {
            return {
                title: filingData.title.plainText,
                taxFilingStatus: filingData.taxFilingStatus,
                effectiveFrom: filingData.effectiveFrom ? convertServerDateToFormDate(filingData.effectiveFrom) : '',
            };
        }

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = tomorrow.toISOString().split('T')[0];

        return {
            title: '',
            taxFilingStatus: TaxFilingStatus.SINGLE,
            effectiveFrom: tomorrowStr,
        };
    }, []);

    const form = useForm<FormValues>({
        resolver: yupResolver(formSchema),
        defaultValues: getDefaultValues(filing),
        mode: 'onSubmit',
    });

    useEffect(() => {
        form.reset(getDefaultValues(filing));
    }, [filing, form, getDefaultValues]);

    useEffect(() => {
        const timer = setTimeout(() => {
            titleInputRef.current?.focus();
        }, 100);

        return () => clearTimeout(timer);
    }, []);

    const handleSave = (data: FormValues) => {
        onSave(data);
    };

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const isAdding = !filing;
    const modalTitle = isAdding ? 'Add Tax Filing' : 'Edit Tax Filing';

    return (
        <Dialog open={open} onOpenChange={(newOpen) => !newOpen && onCancel()}>
            <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <LuFileText className="h-5 w-5 text-gray-600" />
                        {modalTitle}
                    </DialogTitle>
                    <DialogDescription></DialogDescription>
                </DialogHeader>
                <div className="scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100 hover:scrollbar-thumb-gray-400 px-1">
                    <Form {...form}>
                        <form id="tax-filing-form" onSubmit={form.handleSubmit(handleSave)} className="space-y-4">
                            <FormInput
                                control={form.control}
                                name="title"
                                label="Title"
                                placeholder="Enter filing title"
                                inputRef={titleInputRef}
                            />

                            <FormSelect
                                control={form.control}
                                name="taxFilingStatus"
                                label="Filing Status"
                                placeholder="Select filing status"
                            >
                                {Object.values(TaxFilingStatus).map((status) => (
                                    <SelectItem key={status} value={status}>
                                        {titleCase(status)}
                                    </SelectItem>
                                ))}
                            </FormSelect>

                            <FormInput
                                control={form.control}
                                name="effectiveFrom"
                                label="Effective Date"
                                type="date"
                                disabled={isActive}
                            />
                            {isActive && (
                                <p className="text-xs text-gray-500">
                                    Cannot edit the effective date of the active filing
                                </p>
                            )}
                        </form>
                    </Form>
                </div>
                <DialogFooter>
                    <Button variant="outline" type="button" onClick={onCancel} onMouseDown={(e) => e.preventDefault()}>
                        Cancel
                    </Button>
                    <Button variant="default" type="submit" form="tax-filing-form">
                        {isAdding ? 'Add Filing' : 'Save Changes'}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
